import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { gql } from '@apollo/client'
import { apolloClient } from '../utils/apollo.js'
import { t, getCurrentLanguageId } from '../utils/i18n.js'
import styles from './meaning-etymologies-page.css?inline'
import '../components/etymology-display.js'
import '../components/etymology-root-display.js'
import '../components/meaning-grammeme-display.js'
import '@material/web/button/filled-button.js'
import '@material/web/select/filled-select.js'
import '@material/web/select/select-option.js'

const XAMIX_SYSTEM_ID = '1'

const MEANING_QUERY = gql`
  query Meaning($id: ID!) {
    meaning(id: $id) {
      id
      type { id name }
      language { id name }
      word { id }
    }
  }
`

const SPELLINGS_BY_MEANING_QUERY = gql`
  query SpellingsByMeaningId($meaningId: ID!) {
    spellingsByMeaningId(meaningId: $meaningId) {
      id
      spelling
      system { id name }
    }
  }
`

const GET_ETYMOLOGIES_BY_MEANING_QUERY = gql`
  query GetEtymologiesByMeaningId($meaningId: ID!, $displayLanguageId: ID) {
    etymologiesByMeaningId(meaningId: $meaningId, displayLanguageId: $displayLanguageId) {
      id
      title
      description
      roots {
        id
        rootSpelling
        displaySpelling
        root {
          id
          type { name }
          language { name }
        }
      }
    }
  }
`

const GET_LANGUAGES_QUERY = gql`
  query GetLanguages {
    languages {
      id
      name
    }
  }
`

const MEANINGS_BY_WORD_QUERY = gql`
  query MeaningsByWordId($wordId: ID!, $languageId: ID) {
    meaningsByWordId(wordId: $wordId, languageId: $languageId) {
      id
      type { id name }
      language { id name }
      word { id }
    }
  }
`

const MEANING_GRAMMEMES_QUERY = gql`
  query MeaningGrammemes($meaningId: ID!) {
    meaningGrammemes(meaningId: $meaningId) {
      id
      meaning { id }
      grammeme { id value feature { id name } }
    }
  }
`

/** Mock data for testing meaning-grammeme-display when API is unavailable or for UI dev. */
const MEANING_GRAMMEMES_MOCK: Array<{ id: string; grammeme: { id: string; value: string; feature: { id: string; name: string } } }> = [
  { id: 'mock-1', grammeme: { id: '101', value: 'Absolute', feature: { id: '1', name: 'Category' } } },
  { id: 'mock-2', grammeme: { id: '201', value: 'Singular', feature: { id: '2', name: 'Subject number' } } },
  { id: 'mock-3', grammeme: { id: '1101', value: 'Neutral', feature: { id: '11', name: 'Subject gender' } } },
]

@customElement('meaning-etymologies-page')
export class MeaningEtymologiesPage extends LitElement {
  @property({ type: String }) meaningId = ''

  @state() private loading = false
  @state() private error = ''
  @state() private meaning: { id: string; type: { id: string; name: string }; language: { id: string; name: string }; word: { id: string } } | null = null
  @state() private spellings: Array<{ id: string; spelling: string; system: { id: string; name: string } }> = []
  @state() private languages: Array<{ id: string; name: string }> = []
  @state() private rootSpellingsByMeaningId: Record<string, Array<{ text: string; systemId: string; systemName?: string }>> = {}
  @state() private extraMeanings: Array<{ id: string; type: { id: string; name: string }; language: { id: string; name: string }; word: { id: string } }> = []
  @state() private extraSpellingsByMeaningId: Record<string, Array<{ text: string; systemId: string; systemName?: string }>> = {}
  @state() private etymologies: Array<{
    id: string
    title?: string
    description?: string
    roots: Array<{
      id: string
      rootSpelling?: string
      displaySpelling?: string
      root: { id: string; type: { name: string }; language: { name: string } }
    }>
  }> = []
  @state() private selectedLanguageId = ''
  @state() private addingMeaning = false
  @state() private addMeaningError = ''
  @state() private addMeaningSuccess = ''
  @state() private meaningGrammemes: Array<{ id: string; grammeme: { id: string; value: string; feature: { id: string; name: string } } }> = []
  /** Set to true to use MEANING_GRAMMEMES_MOCK instead of API data (for testing). */
  private useMeaningGrammemesMock = false

  static styles = css`
    ${unsafeCSS(styles)}
  `

  updated(changed: Map<string, unknown>) {
    if (changed.has('meaningId')) {
      this.loadData()
    }
  }

  private async loadData() {
    if (!this.meaningId) return
    this.loading = true
    this.error = ''
    try {
      const displayLanguageId = getCurrentLanguageId() || undefined
      const [meaningRes, spellingsRes, etymRes, languagesRes, mgRes] = await Promise.all([
        apolloClient.query({ query: MEANING_QUERY, variables: { id: this.meaningId } }),
        apolloClient.query({ query: SPELLINGS_BY_MEANING_QUERY, variables: { meaningId: this.meaningId } }),
        apolloClient.query({ query: GET_ETYMOLOGIES_BY_MEANING_QUERY, variables: { meaningId: this.meaningId, displayLanguageId } }),
        apolloClient.query({ query: GET_LANGUAGES_QUERY }),
        apolloClient.query({ 
          query: MEANING_GRAMMEMES_QUERY,
          variables: { meaningId: this.meaningId }
        }),
      ])
      this.meaning = (meaningRes.data as any).meaning ?? null
      this.spellings = (spellingsRes.data as any).spellingsByMeaningId ?? []
      this.etymologies = (etymRes.data as any).etymologiesByMeaningId ?? []
      this.languages = (languagesRes.data as any).languages ?? []
      this.meaningGrammemes = this.useMeaningGrammemesMock
        ? MEANING_GRAMMEMES_MOCK
        : ((mgRes.data as any).meaningGrammemes ?? [])

      const rootIds = Array.from(new Set(
        this.etymologies.flatMap(e => (e.roots ?? []).map((r: any) => r.root?.id).filter(Boolean))
      )) as string[]
      const rootSpellings = await Promise.all(
        rootIds.map(async (rootId) => {
          const res = await apolloClient.query({ query: SPELLINGS_BY_MEANING_QUERY, variables: { meaningId: rootId } })
          const items = (res.data as any).spellingsByMeaningId ?? []
          return [rootId, items.map((s: any) => ({ text: s.spelling, systemId: s.system?.id ?? '', systemName: s.system?.name }))] as const
        })
      )
      this.rootSpellingsByMeaningId = Object.fromEntries(rootSpellings)
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.loading = false
    }
  }

  private getText(key: string, fallback: string) {
    const value = t(key)
    return value === key ? fallback : value
  }

  private renderSpellingRow(spelling: { spelling: string; system: { id: string; name: string } }) {
    return html`
      <div class="meaning-spelling-row">
        ${spelling.system?.id === XAMIX_SYSTEM_ID
        ? html`<xamix-element dir="rtl" text-input="${spelling.spelling}" root="/node_modules/@tlenexkoyotl/xamix-element" font-size="4" bold adaptable></xamix-element>`
        : html`<span>${spelling.spelling}</span>`}
        ${spelling.system?.name ? html`<span class="meaning-spelling-system">· ${t(spelling.system.name)}</span>` : ''}
      </div>
    `
  }

  private get canEdit(): boolean {
    return !!localStorage.getItem('auth-token')
  }

  private renderHeader() {
    if (!this.meaning) {
      return html`<h2>${t('createDialog.meaningOptionLabel', { id: this.meaningId, typeName: '', languageName: '', displaySpelling: '' })}</h2>`
    }
    const spellingPreview = this.spellings[0]?.spelling
    const typeName = t(this.meaning.type?.name ?? '')
    const languageName = t(this.meaning.language?.name ?? '')
    const displaySpelling = spellingPreview ? ` — ${spellingPreview}` : ''
    return html`
      <div class="edit-section">
        <div class="meaning-meta">
          <div class="meaning-header-row">
            <h2 class="section-title">${t('createDialog.meaningOptionLabel', { id: this.meaning.id, typeName, languageName, displaySpelling })}</h2>
            ${this.canEdit ? html`
              <a href="/edit?meaningId=${this.meaningId}" class="edit-meaning-link" @click=${(e: Event) => { e.preventDefault(); this.handleEditMeaning(); }}>
                ${t('nav.edit')}
              </a>
            ` : ''}
          </div>
          ${this.spellings.length > 0 ? html`
            <div class="meaning-spellings">\
              <strong>${this.getText('createDialog.spellings', 'Spellings')} (${languageName})</strong>
              ${this.spellings.map(s => this.renderSpellingRow(s))}
            </div>
          ` : ''}
        </div>
      </div>
    `
  }

  private handleEditMeaning() {
    window.history.pushState({}, '', `/edit?meaningId=${this.meaningId}`)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  private async handleAddMeaning() {
    if (!this.meaning || !this.selectedLanguageId) return
    this.addMeaningError = ''
    this.addMeaningSuccess = ''
    if (this.selectedLanguageId === this.meaning.language.id) {
      this.addMeaningError = t('meaningEtymologies.showMeaningSameLanguage')
      return
    }

    this.addingMeaning = true
    try {
      const meaningsRes = await apolloClient.query({
        query: MEANINGS_BY_WORD_QUERY,
        variables: {
          wordId: this.meaning.word.id,
          languageId: this.selectedLanguageId,
        },
        fetchPolicy: 'network-only',
      })
      const meanings = (meaningsRes.data as any).meaningsByWordId ?? []
      const filtered = meanings.filter((m: any) => m.id !== this.meaning?.id)
      if (filtered.length === 0) {
        this.addMeaningError = t('meaningEtymologies.showMeaningNoResults')
        this.extraMeanings = []
        this.extraSpellingsByMeaningId = {}
        return
      }

      this.extraMeanings = filtered
      const extraSpellings = await Promise.all(
        filtered.map(async (item: any) => {
          const res = await apolloClient.query({ query: SPELLINGS_BY_MEANING_QUERY, variables: { meaningId: item.id } })
          const items = (res.data as any).spellingsByMeaningId ?? []
          return [item.id, items.map((s: any) => ({ text: s.spelling, systemId: s.system?.id ?? '', systemName: s.system?.name }))] as const
        })
      )
      this.extraSpellingsByMeaningId = Object.fromEntries(extraSpellings)
      this.addMeaningSuccess = t('meaningEtymologies.showMeaningLoaded')
    } catch (err: any) {
      this.addMeaningError = err.message || t('meaningEtymologies.showMeaningFailed')
    } finally {
      this.addingMeaning = false
    }
  }

  render() {
    return html`
      <div class="page">
        ${this.loading ? html`<div class="loading">${t('common.loading')}</div>` : ''}
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        ${!this.loading && !this.error ? html`
          ${this.renderHeader()}
          <div class="columns-layout">
          <div class="column">
          <section class="edit-section">
            <h2 class="section-title">${t('meaningEtymologies.showMeaningTitle')}</h2>
            <div class="add-meaning-controls">
              <md-filled-select
                label=${t('createDialog.language')}
                .value=${this.selectedLanguageId}
                @change=${(e: Event) => { this.selectedLanguageId = (e.target as any).value }}
              >
                <md-select-option value=""><div slot="headline">—</div></md-select-option>
                ${this.languages.map(lang => html`
                  <md-select-option value=${lang.id}><div slot="headline">${t(lang.name)}</div></md-select-option>
                `)}
              </md-filled-select>
              <md-filled-button
                ?disabled=${this.addingMeaning || !this.selectedLanguageId}
                @click=${this.handleAddMeaning}
              >
                ${this.addingMeaning ? t('common.loading') : t('meaningEtymologies.showMeaningAction')}
              </md-filled-button>
            </div>
            ${this.addMeaningError ? html`<div class="error">${this.addMeaningError}</div>` : ''}
            ${this.addMeaningSuccess ? html`<div class="success">${this.addMeaningSuccess}</div>` : ''}
            ${this.extraMeanings.length > 0 ? html`
              <div class="added-meanings">
                ${this.extraMeanings.map(item => html`
                  <etymology-root-display
                    .root=${{
        id: item.id,
        typeName: item.type?.name ?? '',
        languageName: item.language?.name ?? '',
        displaySpelling: '',
        spellings: this.extraSpellingsByMeaningId[item.id] ?? [],
      }}
                  ></etymology-root-display>
                `)}
              </div>
            ` : ''}
          </section>
          <section class="edit-section">
            <h2 class="section-title">${t('editDialog.editEtymology')}</h2>
            ${this.etymologies.length === 0 ? html`
              <div class="empty">${t('wordView.empty') || 'No entries'}</div>
            ` : html`
              ${this.etymologies.map(etymology => html`
                <etymology-display
                  .etymology=${{
          id: etymology.id,
          title: etymology.title ?? '',
          description: etymology.description ?? '',
          roots: (etymology.roots ?? []).map(root => ({
            id: root.root?.id ?? '',
            rowId: root.id,
            typeName: root.root?.type?.name ?? '',
            languageName: root.root?.language?.name ?? '',
            displaySpelling: root.displaySpelling ?? root.rootSpelling ?? '',
            spellings: this.rootSpellingsByMeaningId[root.root?.id ?? ''] ?? [],
          })),
        }}
                ></etymology-display>
              `)}
            `}
          </section>
          </div>
          <div class="column">
          <section class="edit-section">
            ${this.meaningGrammemes.length > 0 ? html`<h2 class="section-title">${t('meaningGrammemes.sectionTitle')}</h2>
            <meaning-grammeme-display .items=${this.meaningGrammemes}></meaning-grammeme-display>` : ''}
          </section>
          </div>
          </div>
        ` : ''}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'meaning-etymologies-page': MeaningEtymologiesPage
  }
}
